import {
  regionHaInstanceApi,
  useAppSelector,
  useAppDispatch,
  IsFullAccess,
  formatQueryRes,
  useLazyGetHaInstanceAccessUrlQuery,
  useGetVmLiveStatusQuery,
  redirectVmUrl
} from '@monorepo/shared';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonToolbar, FlexboxGrid, Stack, useToaster } from 'rsuite';
import { useEffect, useState } from 'react';
import { AiOutlineDisconnect } from 'react-icons/ai';

import { selectDebugKey } from '@monorepo/shared/store/authSlice';
import DetailIcon from '@rsuite/icons/Detail';
import { IoEnterOutline } from 'react-icons/io5';
import { VscTerminalCmd } from 'react-icons/vsc';
import { AssetIndicator } from './AssetList';
import {
  columnLeftWithHeight,
  dataStyle,
  slimText,
  styleCenterWithHeight,
  titleStyle
} from './AccountDetail';
import { ErrorToaster, InstanceStatus } from './InstanceStatus';
import { getAction } from './InstanceDetail';
import { HomeElms } from '../../config/const';
import { selectHaGroups } from '@monorepo/shared/store/groupSlice';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';

interface IProps {
  item: Record<string, any>;
  account: Record<string, any>;
  isCluster?: boolean;
}
export const InstanceItemRender = ({ item, account, isCluster }: IProps) => {
  const { groupId } = useParams();
  const haGroups = useAppSelector(selectHaGroups);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [disableEnter, setDisableEnter] = useState<boolean>(false);
  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [isAccountFullAccess, setIsAccountFullAccess] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [
    getInstanceUrl,
    { data, isLoading, isFetching, isError, error, isSuccess }
  ] = useLazyGetHaInstanceAccessUrlQuery();
  const toaster = useToaster();
  const debugKey = useAppSelector(selectDebugKey);
  const goToDetail = (id: string) => {
    navigate(`instance/${id}`);
  };
  const goToWebssh = () => {
    if (window) {
      // remove previous sid if exists
      const prevSid = sessionStorage.getItem('lastSid');
      if (prevSid) {
        sessionStorage.removeItem(prevSid);
      }
      const data = {
        account_id: account?.account_id || -1,
        user_id: account?.user_id,
        access_from_user_id:
          account.access_from_user_id || account?.access_from?.user_id,
        region_url: item?.region?.url || '',
        token: item?.region?.token || '',
        instance_id: item?.instance_id || '',
        group_id: account?.group_id || ''
      };
      const dataString = encodeURIComponent(JSON.stringify(data));
      const id = crypto.randomUUID();
      let url = `/webssh/?sid=${id}`;
      sessionStorage.setItem(id, dataString);
      sessionStorage.setItem('lastSid', id);
      if (debugKey) {
        url += `&debug_key=${debugKey}`;
      }
      window.open(url, '_blank');
    }
  };
  // use 'is_active' in get_instance_list fist
  // if instance list is inactive query vm status check again
  const { data: vmStatus, isSuccess: isVmStatusSuccess } =
    useGetVmLiveStatusQuery(
      formatQueryRes({
        account,
        instance: item,
        region: item?.region
      }),
      { skip: !item?.region?.url || item.is_active }
    );
  useEffect(() => {
    if (data?.url) {
      redirectVmUrl(data, '_blank');
    }
    if (error) {
      ErrorToaster({
        toaster,
        error,
        title: 'Fetch instance login URL failed!'
      });
    }
  }, [data, error]);
  useEffect(() => {
    if (getAction(item) !== 0) {
      setIsCheck(true);
      // fix bug: 1138741
      // } else if (!item.is_active && item.in_provision) {
    } else if ((isVmStatusSuccess && !vmStatus?.is_active) || isCheck) {
      setIsCheck(true);
    }
  }, [item, vmStatus]);
  useEffect(() => {
    if (groupId && haGroups?.[groupId]?.permission === 'ssh_only') {
      setDisableEnter(true);
      // } else if (!item?.is_active || isCheck) {
    } else if ((isVmStatusSuccess && !vmStatus?.is_active) || isCheck) {
      setDisableEnter(true);
    } else {
      setDisableEnter(false);
    }
  }, [groupId, haGroups, item, isCheck, vmStatus, isVmStatusSuccess]);
  useEffect(() => {
    if (
      IsFullAccess(currentAccount, HomeElms.fcName) ||
      currentAccount?.user_fullaccess
    ) {
      setIsAccountFullAccess(true);
    } else {
      setIsAccountFullAccess(false);
    }
  }, [currentAccount]);
  return (
    <FlexboxGrid>
      {isCluster && (
        <FlexboxGrid.Item
          colspan={1}
          style={columnLeftWithHeight}
        ></FlexboxGrid.Item>
      )}
      <FlexboxGrid.Item colspan={3} style={columnLeftWithHeight}>
        <Stack alignItems="flex-start" spacing={6}>
          <div style={titleStyle}>{item?.license_sn || ''} </div>
        </Stack>
        <div style={slimText}>
          <Stack spacing={30}>
            <span>Version</span>
            <span
              style={{
                whiteSpace: 'nowrap'
              }}
            >
              {item?.firmware}
            </span>
          </Stack>
          <Stack spacing={10}>
            <span>Expiration Date</span>
            <AssetIndicator
              account_id={account?.account_id}
              sn={item?.license_sn}
              btn_text={item?.expiry_date}
              btn_color={item?.is_expired ? 'red' : 'green'}
            />
          </Stack>
        </div>
      </FlexboxGrid.Item>
      {/*ha data*/}
      <FlexboxGrid.Item colspan={2} style={styleCenterWithHeight}>
        {item?.ha_name ? (
          <div style={{ textAlign: 'center' }}>
            <div style={slimText}>Cluster</div>
            <span style={dataStyle}>{`${item?.ha_name || ''} `}</span>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={slimText}>Region</div>
            <div style={dataStyle}>{`${item?.region?.alias || ''} `}</div>
          </div>
        )}
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={4} style={styleCenterWithHeight}>
        <div style={{ textAlign: 'center' }}>
          <div style={slimText}>Status</div>

          {isCheck ? (
            <Stack spacing={6}>
              {item?.in_provision && !getAction(item) && (
                <div>Provisioning</div>
              )}
              <InstanceStatus
                action={getAction(item)}
                group_id={account?.group_id}
                account_id={account?.account_id}
                user_id={account?.user_id || ''}
                access_from_user_id={account?.access_from?.user_id || ''}
                instance_id={item?.instance_id}
                license_sn={item?.license_sn}
                region={item?.region}
                callBackFn={(obj: any) => {
                  dispatch(
                    regionHaInstanceApi.util.invalidateTags(['RegionInstance'])
                  );
                  // dispatch(
                  //   regionHaInstanceApi.util.invalidateTags([
                  //     { type: 'VmStatus', id: item?.instance_id }
                  //   ])
                  // );
                }}
              />
            </Stack>
          ) : (
            <div style={dataStyle}>
              {item?.is_active ? (
                <div>Active</div>
              ) : (
                <Stack alignItems="flex-start">
                  <div>Inactive</div>
                  <AiOutlineDisconnect
                    title="instance is inactive"
                    style={{ color: 'red', fontSize: '16px' }}
                  />
                </Stack>
              )}
            </div>
          )}
        </div>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={2} style={styleCenterWithHeight}>
        <div style={{ textAlign: 'center' }}>
          <div style={slimText}>Data Center</div>
          <div style={dataStyle}>{item?.project_name || ''}</div>
        </div>
      </FlexboxGrid.Item>
      {item?.ha_id && item?.cluster_info?.health_status_text ? (
        <FlexboxGrid.Item colspan={2} style={styleCenterWithHeight}>
          <div style={{ textAlign: 'center' }}>
            <div style={slimText}>Health</div>
            <div style={dataStyle}>
              {item?.cluster_info?.health_status_text}
            </div>
          </div>
        </FlexboxGrid.Item>
      ) : (
        <FlexboxGrid.Item colspan={2}></FlexboxGrid.Item>
      )}
      {item?.ha_id && item?.cluster_info?.node_role ? (
        <FlexboxGrid.Item colspan={2} style={styleCenterWithHeight}>
          <div style={{ textAlign: 'center' }}>
            <div style={slimText}>Node Role</div>
            <div style={dataStyle}>{item?.cluster_info?.node_role}</div>
          </div>
        </FlexboxGrid.Item>
      ) : (
        <FlexboxGrid.Item colspan={2}></FlexboxGrid.Item>
      )}
      <FlexboxGrid.Item
        colspan={8}
        style={{
          ...styleCenterWithHeight,
          justifyContent: 'flex-end'
        }}
      >
        <ButtonToolbar>
          <Button
            startIcon={<DetailIcon />}
            appearance="ghost"
            // disabled={!item?.is_active}
            size="xs"
            onClick={() => goToDetail(item?.instance_id)}
          >
            Manage Instance
          </Button>
          <Button
            startIcon={<VscTerminalCmd />}
            appearance="ghost"
            // disabled on snapshot create and revert or not full access
            disabled={
              getAction(item) !== 0 ||
              (!item?.is_active && !vmStatus?.is_active) ||
              !isAccountFullAccess
            }
            size="xs"
            onClick={() => goToWebssh()}
          >
            WebSSH
          </Button>
          {item?.ha_id && item.ha_status === 'IDLE' ? (
            <></>
          ) : (
            <Button
              loading={isLoading || isFetching}
              startIcon={<IoEnterOutline />}
              appearance="primary"
              size="xs"
              disabled={disableEnter}
              onClick={() => {
                getInstanceUrl({
                  account: account,
                  instance: item,
                  region: item.region
                });
              }}
            >
              Enter
            </Button>
          )}
        </ButtonToolbar>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};
